import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  endpoints,
  APIError,
  type EntityChannelRef,
  type EntityMergeHistoryItem,
} from "@/lib/api";
import { useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { fmtNumber, fmtRelativeDays, platformLabel } from "@/lib/format";
import { EntityPicker, CreateEntityInline } from "@/components/EntityPicker";

// EntitySelf — v2.8.0 Day 7 — fixed-URL alias for the Bitbase self entity.
// Layout matches EntityDetail (so operators get the same affordances), but
// the data path goes through /api/entities/self which encapsulates the
// "resolve singleton" + "is_internal=true" semantics on the server.
//
// 3 server states drive 3 UI shapes:
//   - 200 → render the entity (full grid + merge history)
//   - 404 → show a seed CTA (CreateEntityInline with defaultIsInternal +
//           lockIsInternal so the is_internal=true checkbox can't be
//           skipped). Lets the operator bootstrap from this tab without
//           dropping to SQL.
//   - 409 → multiple internal entities exist. Show direct links to each
//           conflicting entity (from the 409 body's ids[]) + a deep link
//           to /entities?is_internal=true so the existing merge UX can
//           consolidate them.
//
// This is the operator-facing entry point for the "Bitbase 自身" sub-product
// (entities-design.md 流程 2). Future tabs (content calendar, cross-platform
// health) hang off this page; see BACKLOG for the roadmap.

export default function EntitySelf() {
  const qc = useQueryClient();
  const selfQ = useQuery({
    queryKey: ["entities", "self"],
    queryFn: () => endpoints.selfEntity(),
    refetchOnWindowFocus: false,
    // Don't auto-retry 404/409 — those are deterministic states the UI
    // surfaces explicitly. Retrying would just rate-limit the user.
    retry: (n, err) => {
      if (err instanceof APIError && (err.status === 404 || err.status === 409)) {
        return false;
      }
      return n < 2;
    },
  });

  const [movingChannel, setMovingChannel] = useState<EntityChannelRef | null>(null);

  const moveMut = useMutation({
    mutationFn: ({
      channelID,
      toEntityID,
      reason,
    }: {
      channelID: number;
      toEntityID: number;
      reason: string;
    }) =>
      endpoints.moveChannelToEntity(channelID, toEntityID, {
        reason: reason || undefined,
      }),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["entities", "self"] });
      qc.invalidateQueries({ queryKey: ["entities", "list"] });
      qc.invalidateQueries({ queryKey: ["entities", "detail", resp.to_entity_id] });
      qc.invalidateQueries({ queryKey: ["channel"] });
    },
  });

  if (selfQ.isLoading) {
    return <p className="p-6 text-sm text-muted-foreground">加载中…</p>;
  }

  if (selfQ.isError && selfQ.error instanceof APIError) {
    if (selfQ.error.status === 404) {
      return <NotSeeded onCreated={() => qc.invalidateQueries({ queryKey: ["entities", "self"] })} />;
    }
    if (selfQ.error.status === 409) {
      const body = selfQ.error.detail as
        | { total?: number; ids?: number[] }
        | undefined;
      return (
        <Conflict
          message={selfQ.error.message}
          conflictingIDs={body?.ids ?? []}
          total={body?.total}
        />
      );
    }
  }

  if (selfQ.isError) {
    return (
      <p className="p-6 text-sm text-destructive">
        加载失败:{(selfQ.error as Error).message}
      </p>
    );
  }

  const entity = selfQ.data;
  if (!entity) return null;

  return (
    <div className="flex h-full flex-col overflow-auto">
      {/* Header — same layout as EntityDetail but fixed URL ribbon up top */}
      <div className="border-b border-border bg-background px-6 py-4">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Bitbase 自身 · self-monitor 子产品
            </p>
            <div className="mt-1 flex items-center gap-3">
              <h1 className="text-2xl font-semibold">{entity.canonical_name}</h1>
              <Badge className="bg-amber-100 text-amber-700">★ 我们</Badge>
            </div>
            <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
              <div className="flex gap-1.5">
                <dt>entity #:</dt>
                <dd className="text-foreground">{entity.id}</dd>
              </div>
              {entity.primary_handle && (
                <div className="flex gap-1.5">
                  <dt>primary_handle:</dt>
                  <dd className="font-mono text-foreground">{entity.primary_handle}</dd>
                </div>
              )}
              <div className="flex gap-1.5">
                <dt>创建:</dt>
                <dd className="text-foreground">{fmtRelativeDays(entity.created_at)}</dd>
              </div>
            </dl>
            {entity.notes && (
              <p className="mt-2 whitespace-pre-wrap rounded border border-border bg-muted/40 px-3 py-2 text-sm text-foreground">
                {entity.notes}
              </p>
            )}
          </div>
          <Link
            to={`/entities/${entity.id}`}
            className="text-xs text-muted-foreground hover:underline"
            title="canonical detail URL — same data, persistent across self-entity reassignment"
          >
            entity #{entity.id} 详情 →
          </Link>
        </div>
      </div>

      {/* Channels grid */}
      <section className="border-b border-border bg-background px-6 py-4">
        <h2 className="mb-3 text-sm font-medium text-muted-foreground">
          自家平台账号 ({entity.channels.length})
        </h2>
        {entity.channels.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            还没有任何自家账号关联。在{" "}
            <Link to="/import" className="text-primary hover:underline">
              CSV 导入
            </Link>{" "}
            或{" "}
            <Link to="/channels" className="text-primary hover:underline">
              频道库
            </Link>{" "}
            添加 channel,然后在 channel 详情页"归属 entity"块点"移动到 entity…"选这个。
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {entity.channels.map((c) => (
              <ChannelCard key={c.id} channel={c} onMove={() => setMovingChannel(c)} />
            ))}
          </div>
        )}
      </section>

      {/* Aggregation roadmap placeholder. Intentionally vague — the
          ideas live in BACKLOG with no scheduled delivery. Past versions
          baked a "Day 8-11" commitment into this section; that wasn't
          honest and would set the operator up to ask "where is it" on a
          fake timeline. Drop or update when the work is actually
          scheduled. */}
      <section className="border-b border-border bg-background px-6 py-4">
        <h2 className="mb-2 text-sm font-medium text-muted-foreground">
          跨平台聚合(BACKLOG · 暂无排期)
        </h2>
        <p className="text-xs text-muted-foreground">
          跨平台曝光 · 发布日历 · 健康度告警等聚合视图,设计在 docs/BACKLOG.md。这一段会在配套查询 ship 时填实。
        </p>
      </section>

      {/* Merge history — usually empty for self entity unless the operator
          consolidated multiple internal entities at some point */}
      <section className="bg-background px-6 py-4">
        <h2 className="mb-2 text-sm font-medium text-muted-foreground">
          合并历史 ({entity.merge_history.length})
        </h2>
        {entity.merge_history.length === 0 ? (
          <p className="text-xs text-muted-foreground">还没有合并记录。</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {entity.merge_history.map((h) => (
              <MergeHistoryRow key={h.history_id} item={h} />
            ))}
          </ul>
        )}
      </section>

      <EntityPicker
        open={movingChannel !== null}
        channelLabel={movingChannel?.handle ?? movingChannel?.name ?? `#${movingChannel?.id ?? ""}`}
        excludeEntityID={entity.id}
        onClose={() => setMovingChannel(null)}
        onPick={async (toEntityID, reason) => {
          if (!movingChannel) return;
          await moveMut.mutateAsync({
            channelID: movingChannel.id,
            toEntityID,
            reason,
          });
          setMovingChannel(null);
        }}
      />
    </div>
  );
}

function ChannelCard({
  channel,
  onMove,
}: {
  channel: EntityChannelRef;
  onMove: () => void;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span>{platformLabel(channel.platform)}</span>
          {channel.lifecycle_status === "active" ? (
            <Badge variant="secondary">{channel.lifecycle_status}</Badge>
          ) : (
            <Badge>{channel.lifecycle_status}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        <div className="font-mono text-xs text-muted-foreground">
          {channel.handle ?? channel.platform_channel_id}
        </div>
        <div className="font-medium">{channel.name ?? "—"}</div>
        {channel.follower_count !== undefined && (
          <div className="text-xs text-muted-foreground">
            {fmtNumber(channel.follower_count)} 粉丝
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <Link
            to={`/channels/${channel.id}`}
            className="text-xs text-primary hover:underline"
          >
            详情 →
          </Link>
          <button
            type="button"
            onClick={onMove}
            className="ml-auto text-xs text-primary hover:underline"
          >
            移动到 entity…
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

function MergeHistoryRow({ item }: { item: EntityMergeHistoryItem }) {
  const channelLabel =
    item.channel_handle ?? item.channel_name ?? `channel #${item.channel_id}`;
  const fromLabel = item.from_entity_canonical_name
    ? `${item.from_entity_canonical_name} (entity #${item.from_entity_id})`
    : `entity #${item.from_entity_id}`;
  return (
    <li className="flex items-baseline gap-2 text-sm">
      <span className="font-mono text-xs text-muted-foreground">
        {fmtRelativeDays(item.created_at)}
      </span>
      <span>
        <Link
          to={`/channels/${item.channel_id}`}
          className="font-medium text-foreground hover:underline"
        >
          {channelLabel}
        </Link>{" "}
        <span className="text-muted-foreground">由 {fromLabel} 移入</span>
        <span className="text-muted-foreground"> · by {item.actor}</span>
        {item.reason && (
          <span className="text-muted-foreground"> · {item.reason}</span>
        )}
      </span>
    </li>
  );
}

function NotSeeded({ onCreated }: { onCreated: () => void }) {
  return (
    <div className="mx-auto max-w-lg space-y-4 p-6">
      <div className="rounded-md border border-border bg-background p-4">
        <h2 className="text-base font-semibold">Bitbase 自身 entity 还没创建</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          这个 tab 需要一个 <span className="font-mono">is_internal=true</span> 的 entity 作为锚点。
          下方表单已为你锁定 <span className="font-mono">is_internal</span>,创建之后通过任意 channel 详情页的"移动到 entity…"把自家账号挂到这下面。
        </p>
      </div>
      <div className="rounded-md border border-border bg-background p-4">
        <CreateEntityInline
          defaultName="Bitbase"
          defaultIsInternal
          lockIsInternal
          hideCancel
          onCancel={() => undefined}
          onCreated={onCreated}
        />
      </div>
    </div>
  );
}

function Conflict({
  message,
  conflictingIDs,
  total,
}: {
  message: string;
  conflictingIDs: number[];
  total?: number;
}) {
  return (
    <div className="mx-auto max-w-lg p-6">
      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4">
        <h2 className="text-base font-semibold text-destructive">
          有多个 is_internal=true 的 entity
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{message}</p>
        {conflictingIDs.length > 0 && (
          <div className="mt-3">
            <p className="text-xs text-muted-foreground">
              {total ?? conflictingIDs.length} 个冲突 entity:
            </p>
            <ul className="mt-1 flex flex-wrap gap-2 text-xs">
              {conflictingIDs.map((id) => (
                <li key={id}>
                  <Link
                    to={`/entities/${id}`}
                    className="rounded border border-border bg-background px-2 py-0.5 text-primary hover:underline"
                  >
                    entity #{id}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
        <Link
          to="/entities?is_internal=true"
          className="mt-3 inline-block text-sm text-primary hover:underline"
        >
          打开筛选列表(已预选 ★ 我们的) →
        </Link>
        <div className="mt-2 space-y-1 text-xs text-muted-foreground">
          <p>
            修复路径:
          </p>
          <ul className="ml-4 list-disc space-y-0.5">
            <li>
              <span className="font-medium">有 channel 的 entity</span>:在每个 channel 详情页"移动到 entity…"挪到留下来的那一个;最后一个 channel 移出后 orphan-clean 自动删空 entity(internal 的现在受保护不会被删)。
            </li>
            <li>
              <span className="font-medium">空的 entity</span>:目前需直接 SQL <code className="rounded bg-muted px-1">DELETE FROM entities WHERE id = X AND is_internal = TRUE AND NOT EXISTS (SELECT 1 FROM channels WHERE entity_id = X)</code>(将来加 DELETE 端点)。
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
